import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { API, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatApiError(detail) {
    if (!detail) return "Något gick fel.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function AterstallLosenord() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Lösenordet måste vara minst 8 tecken");
            return;
        }
        if (password !== confirm) {
            toast.error("Lösenorden matchar inte");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(
                `${API}/auth/reset-password`,
                { token, password },
                { withCredentials: true }
            );
            setUser(data.user);
            toast.success("Lösenord uppdaterat — du är inloggad");
            navigate(data.user.role === "admin" ? "/admin" : "/portal", {
                replace: true,
            });
        } catch (err) {
            toast.error(
                formatApiError(err.response?.data?.detail) ||
                    "Länken är ogiltig eller har gått ut"
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="page-aterstall-losenord"
            className="relative min-h-[80vh] grid place-items-center py-20"
        >
            <div className="relative max-w-lg w-full mx-4 bg-[#111113] border border-white/10 rounded-sm p-10 lg:p-14">
                <div className="w-14 h-14 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                    <Lock size={22} />
                </div>
                <p className="mt-6 overline">Återställ lösenord</p>
                <h1 className="mt-3 font-serif font-light text-3xl sm:text-4xl tracking-tight leading-[1.15]">
                    Välj ett{" "}
                    <span className="italic text-[#C4A977]">nytt lösenord</span>
                </h1>
                <p className="mt-5 text-sm text-[#A1A1AA] leading-relaxed">
                    Ange ett nytt lösenord (minst 8 tecken). När du sparat
                    loggas du in automatiskt.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                            Nytt lösenord
                        </Label>
                        <Input
                            data-testid="reset-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] h-11 mt-2"
                            autoComplete="new-password"
                            minLength={8}
                            required
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                            Bekräfta lösenord
                        </Label>
                        <Input
                            data-testid="reset-confirm"
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            className="bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] h-11 mt-2"
                            autoComplete="new-password"
                            minLength={8}
                            required
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="reset-submit"
                        className="w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 text-sm tracking-wide font-medium"
                    >
                        {submitting ? "Sparar..." : "Spara nytt lösenord"}
                    </Button>
                </form>

                <Link
                    to="/logga-in"
                    className="mt-6 inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F4F4F5] text-sm"
                >
                    ← Tillbaka till inloggning
                </Link>

                <div className="mt-8 flex items-start gap-3 text-xs text-[#A1A1AA]/80">
                    <ShieldCheck
                        size={14}
                        className="text-[#C4A977] mt-0.5 shrink-0"
                    />
                    <p>
                        När du sparar loggas alla dina existerande sessioner ut
                        — på alla enheter — för säkerhets skull.
                    </p>
                </div>
            </div>
        </section>
    );
}
